import { config } from '@/config';
import appbarSlice from '@/redux/slice/appbar.slice';
import authSlice from '@/redux/slice/auth.slice';
import highlightPreviewSlice from '@/redux/slice/highlightPreview.slice';
import ruleSlice from '@/redux/slice/rule.slice';
import rulesSlice from '@/redux/slice/rules.slice';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import storageSession from 'redux-persist/lib/storage/session';
import { apiCaller, blackoutHolidaysApiCaller, cmsApiCaller, locationApiCaller } from './query';
import bannerSlice from './slice/banner.slice';
import cacheSlice from './slice/cache.slice';
import errorSlice from './slice/error.slice';
import loadingSlice from './slice/loading';
import navigateSlice from './slice/navigate';
import onboardingSlice from './slice/onboarding.slice';
import previewSlice from './slice/preview.slice';
import sessionSlice from './slice/session.slice';
import settingsSlice from './slice/settings.slice';
import toastSlice from './slice/toast.slice';
import languageRuleSlice from '@/redux/slice/languageRule.slice';
import filterRulesSlice from '@/redux/slice/filterRules.slice';
import popupSlice from './slice/popup.slice';
import checkoutRuleSlice from '@/redux/slice/checkoutRule.slice';

const toastPersistConfig = {
  key: 'toast',
  storage,
};
const bannerPersistConfig = {
  key: `${config.shop}-banner`,
  storage,
};
const settingsPersistConfig = {
  key: `${config.shop}-settings`,
  storage,
  whitelist: ['activeKey', 'rule', 'modeLight'],
};

const sessionPersistConfig = {
  key: 'session',
  storage: storageSession,
};

const persistRulesConfig = {
  key: 'rule',
  storage: storageSession,
};

const persistLanguageRuleConfig = {
  key: 'languageRule',
  storage,
};

const persistPopupConfig = {
  key: 'popup',
  storage,
};

const rootReducer = combineReducers({
  // Thêm Reducer tại đây. Sample : sample : sampleReducer
  toast: persistReducer(toastPersistConfig, toastSlice.reducer),
  loading: loadingSlice.reducer,
  banner: persistReducer(bannerPersistConfig, bannerSlice.reducer),
  error: errorSlice.reducer,
  settings: persistReducer(settingsPersistConfig, settingsSlice.reducer),
  navigate: navigateSlice.reducer,
  apiCaller: apiCaller.reducer,
  locationApiCaller: locationApiCaller.reducer,
  blackoutHolidaysApiCaller: blackoutHolidaysApiCaller.reducer,
  cmsApiCaller: cmsApiCaller.reducer,
  cache: cacheSlice.reducer,
  onboarding: onboardingSlice.reducer,
  appbar: appbarSlice.reducer,
  session: persistReducer(sessionPersistConfig, sessionSlice.reducer),
  preview: previewSlice.reducer,
  highlightPreview: highlightPreviewSlice.reducer,
  rule: ruleSlice.reducer,
  rules: persistReducer(persistRulesConfig, rulesSlice.reducer),
  auth: authSlice.reducer,
  filterRules: filterRulesSlice.reducer,
  languageRule: persistReducer(persistLanguageRuleConfig, languageRuleSlice.reducer),
  popup: persistReducer(persistPopupConfig, popupSlice.reducer),
  checkoutRule: checkoutRuleSlice.reducer,
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const customizeMiddleware = getDefaultMiddleware({
      serializableCheck: false,
    });
    return customizeMiddleware.concat(
      apiCaller.middleware,
      locationApiCaller.middleware,
      blackoutHolidaysApiCaller.middleware,
      cmsApiCaller.middleware,
    );
  },
});

// setupListeners(store.dispatch)
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const persistor = persistStore(store);
export default store;
